import { useState } from 'react';

export function useSessionStorage<T>(key: string, initialValue: T): [T, (value: T | ((prev: T) => T)) => void] {
  // Get from session storage then
  // parse stored json or return initialValue
  const readValue = () => {
    try {
      // Check if we're in a browser context
      if (typeof window === 'undefined' || !window.sessionStorage) {
        return initialValue;
      }

      try {
        const item = window.sessionStorage.getItem(key);
        const parsedItem = item ? JSON.parse(item) : initialValue;
        return parsedItem;
      } catch (error) {
        console.warn(`Error reading sessionStorage key "${key}":`, error);
        return initialValue;
      }
    } catch (storageError) {
      // If we can't access storage at all, return initial value
      console.warn('Storage access denied:', storageError);
      return initialValue;
    }
  };

  const [storedValue, setStoredValue] = useState<T>(readValue);

  const setValue = (value: T | ((prev: T) => T)) => {
    try {
      // Allow value to be a function so we have the same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value;

      // Save state
      setStoredValue(valueToStore);
      
      // Only try to save to storage if we have access
      if (typeof window !== 'undefined' && window.sessionStorage) {
        try {
          window.sessionStorage.setItem(key, JSON.stringify(valueToStore));
        } catch (error) {
          console.warn(`Error setting sessionStorage key "${key}":`, error);
        }
      } else {
        console.warn(`Cannot save to sessionStorage (not available): ${key}`);
      }
    } catch (error) {
      console.warn(`Error handling storage operation for key "${key}":`, error);
    }
  };

  return [storedValue, setValue];
} 